import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { catchError, from, Observable, switchMap, throwError } from 'rxjs';

import { Store } from '@ngrx/store';

import { selectSelectedWorkSpace, selectTokenAuthentication } from '../../../store/app.selectors';

import { NO_API_KEY, NO_MESSAGING_KEY } from '../../../core/services/http.context';
import { MessagingEnvironmentService } from '../../../core/services/messaging-environment.service';
import { IWorkspace } from '../../chat/interfaces/IWorkspace';
import { AppStateWithChat } from '../../chat/store/chat.reducer';
import { AuthenticationService } from './authentication.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  selectedWorkspace: IWorkspace | null = null;
  tokenAuthentication: string | null = null;
  constructor(
    private auth: AuthenticationService,
    private store: Store<AppStateWithChat>,
    private env: MessagingEnvironmentService
  ) {
    this.setChatSubscription();
  }

  setChatSubscription(): void {
    this.store.select(selectSelectedWorkSpace).subscribe((selectedWorkspace) => {
      this.selectedWorkspace = selectedWorkspace;
    });
    this.store.select(selectTokenAuthentication).subscribe((tokenAuthentication) => {
      this.tokenAuthentication = tokenAuthentication;
    });
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const messagingUUID = this.messagingUUIDForNotificationApi(request);
    if (messagingUUID) {
      request = request.clone({
        headers: request.headers.set('X-Application-Id', messagingUUID),
      });
    }

    if (request.context.get(NO_API_KEY) || request.url.includes('/assets/')) {
      return next.handle(request);
    }

    const token = localStorage.getItem('messaging-token');
    this.auth.checkTokens();
    const messagingApiToken = localStorage.getItem('messaging-api-token');
    if (!messagingApiToken && token && !request.context.get(NO_MESSAGING_KEY) && this.env.env.useHandshake) {
      return this.auth.getMessagingApiToken().pipe(
        switchMap(() => {
          return this.handleRequest(request, token, next);
        })
      );
    }

    return this.handleRequest(request, token, next);
  }

  private handleRequest(
    request: HttpRequest<unknown>,
    token: string | null,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const messagingApiToken = localStorage.getItem('messaging-api-token');
    let dupReq = request.clone({});
    if (!messagingApiToken || request.context.get(NO_MESSAGING_KEY)) {
      dupReq = request.clone({
        headers: request.headers.set(
          `${this.tokenAuthentication || this.selectedWorkspace?.tokenAuthentication || 'x-authorization-truvideo'}`,
          `Bearer ${token ?? ''}`
        ),
      });
    }

    if (messagingApiToken && !request.context.get(NO_MESSAGING_KEY) && !this.tokenAuthentication) {
      dupReq = dupReq.clone({
        headers: dupReq.headers.set(`x-authorization-messaging`, `Bearer ${messagingApiToken}`),
      });
    }

    if (request.url.includes('/auth/refresh')) {
      return next.handle(dupReq);
    }

    return next.handle(dupReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // A service using the original TruVideo credential cannot be repaired
        // by refreshing the separate Messaging credential. Propagate its denial
        // to the caller without invalidating an otherwise valid Messaging session.
        if (error.status === 401 && !request.context.get(NO_MESSAGING_KEY) && !this.isLogsApiRequest(request)) {
          return this.handle401Error(dupReq, next);
        }
        return throwError(error);
      })
    );
  }

  private messagingUUIDForNotificationApi(request: HttpRequest<unknown>): string | undefined {
    if (!this.isNotificationApiRequest(request)) {
      return undefined;
    }
    return this.env.env.messagingUUID;
  }

  private isNotificationApiRequest(request: HttpRequest<unknown>): boolean {
    return !!this.env.env.notificationApi && request.url.includes(this.env.env.notificationApi);
  }

  private isLogsApiRequest(request: HttpRequest<unknown>): boolean {
    return !!this.env.env.logsApi && request.url.includes(this.env.env.logsApi);
  }

  private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const refreshToken = localStorage.getItem('messaging-api-refresh-token');
    const dealerId = localStorage.getItem('dealerId');

    if (!refreshToken) {
      this.auth.logout(dealerId, true);
      return throwError('No refresh token available');
    }

    return this.auth.refreshMessagingApiToken(refreshToken).pipe(
      switchMap((newToken: string) => {
        localStorage.setItem('messaging-api-token', newToken);
        const newRequest = request.clone({
          headers: request.headers.set(`x-authorization-messaging`, `Bearer ${newToken}`),
        });
        return next.handle(newRequest);
      }),
      catchError(() => {
        return from(this.auth.getMessagingApiToken()).pipe(
          switchMap(() => {
            const newToken = localStorage.getItem('messaging-api-token');
            if (!newToken) {
              throw new Error('Failed to retrieve new token');
            }
            const newRequest = request.clone({
              headers: request.headers.set(`x-authorization-messaging`, `Bearer ${newToken}`),
            });
            return next.handle(newRequest);
          }),
          catchError((getTokenError) => {
            this.auth.logout(dealerId, true);
            return throwError(getTokenError);
          })
        );
      })
    );
  }
}
